/* eslint-disable @next/next/no-img-element */
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import type { SkeletonProps } from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { capitalize } from '@mui/material/utils';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Image from "next/image"
import { PFSzalagKiemelesVariant, PriceQuantity } from "../../../client/swaggerClient";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RoomIcon from '@mui/icons-material/Room';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EmailIcon from '@mui/icons-material/Email';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Link from "next/link";
import dynamic from "next/dynamic";
import { FindButtonProps, FindItemDateProps, FindItemDeliveryContainerProps, FindItemDeliveryItemProps, FindItemFactProps, FindItemGridProps, FindItemImageProps, FindItemNoImageProps, FindItemPriceProps, FindItemTextProps, FindTitleProps } from "./FindDesignTypes";
import { useLayoutData } from "../../../utils/contexts/LayoutContext";
import { useMemo } from "react";
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { getPriceFlagString, pfSzalagKiemelesVariantTitle, priceQuantityTitle } from "../../../utils/functions/enumTitles";
import { useRuntimeConfig } from "../../../utils/contexts/RuntimeContext/RuntimeConfigContext";
import { imageURL } from "../../../utils/functions/imageURL";
import { PageType } from "../../../pages/_app";
import { getSzalagImageUrl } from "../../../utils/functions/szalagImageUrls";
import { FindItemProps } from "./Find.types";
import { priceFormatter } from "../../../utils/functions/priceFormatter";
import { useTranslation } from "next-i18next";
import useSessionStorage from "../../../hooks/useSessionStorage";
import { StyledLink } from "./Find.styles";
import moment from 'moment';

const separatedFact = (fact: string) => {
  const index = fact.indexOf(':');
  if (index !== -1) {
    const elsoElem = fact.substring(0, index);
    const masodikElem = fact.substring(index + 1);
    return `<i>${elsoElem}:</i> ${masodikElem}`;
  } else {
      return fact;
  }
}

const FindItem = ({
  item,
  colors,
  isLoading = false,
  setFavButton = true,
  isKiemelt = false,
  isKiemeltFromLanding = false,
  preload = false
}: FindItemProps) => {
  const { partnerSiteFull, templatePath } = useLayoutData()
  const runtimeConfig = useRuntimeConfig()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation()

  const [, setListingScrollPosition] = useSessionStorage<number>('listingScrollPosition', 0)

  const path = templatePath === PageType.RBhuPP ? `${templatePath}/templates/${partnerSiteFull?.content?.templateId ?? 'AutoraTemplate'}` : templatePath
  const type = templatePath

  const FindButton = useMemo(() => dynamic<FindButtonProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindButton)), [path])
  const FindItemContainer = useMemo(() => dynamic<FindItemGridProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemContainer)), [path])
  const FindItemDate = useMemo(() => dynamic<FindItemDateProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemDate)), [path])
  const FindItemDeliveryContainer = useMemo(() => dynamic<FindItemDeliveryContainerProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemDeliveryContainer)), [path])
  const FindItemDeliveryItem = useMemo(() => dynamic<FindItemDeliveryItemProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemDeliveryItem)), [path])
  const FindItemFact = useMemo(() => dynamic<FindItemFactProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemFact)), [path])
  const FindItemImage = useMemo(() => dynamic<FindItemImageProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemImage)), [path])
  const FindItemNoImage = useMemo(() => dynamic<FindItemNoImageProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemNoImage)), [path])
  const FindItemPrice = useMemo(() => dynamic<FindItemPriceProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemPrice)), [path])
  const FindItemText = useMemo(() => dynamic<FindItemTextProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindItemText)), [path])
  const FindTitle = useMemo(() => dynamic<FindTitleProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindTitle)), [path])
  const FindSkeleton = useMemo(() => dynamic<SkeletonProps>(() => import(`../../../design/${path}/_findDesign/Design`).then((style) => style.FindSkeleton)), [path])
  
  const hasQuantity = Boolean(item?.price?.quantity)
	const hasFlags = Boolean(item?.price?.flags)
  const hasUrl = item?.url && item?.url !== "" || item?.urlPP && item.urlPP !== ""
  const hasDisplayedAt = item?.displayedAt
  const hasSzalagKiemeles = (type === PageType.SZP || type === PageType.SZPPL) && item?.kiemelesSzalag
  const hasKeyFacts = item?.keyFacts && item.keyFacts.length > 0
  const justOneKeyFact = item?.keyFacts && item.keyFacts.length === 1 ? 1 : 0
  const hasText = item?.text && item.text !== ""
  const hasKeyFactsOrText = hasKeyFacts || hasText
  const hasShipping = item?.shippingFutar || item?.shippingPosta || item?.shippingSzemelyes

  const imageUrl = isMobile ? 
    imageURL(runtimeConfig.BLOB_BASE_PATH, item?.imageUrlL) : 
    imageURL(runtimeConfig.BLOB_BASE_PATH, item?.imageUrlS)

  const handleSetPosition = () => {

    setListingScrollPosition(window.scrollY)
  }

  return (
    <>
      <FindItemContainer container iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0} alignContent={"flex-start"} sx={{ backgroundColor: isKiemelt ? `${theme.palette.primary.light}7f` : undefined }} height={isKiemeltFromLanding ? "100%" : undefined}>
        <Grid item xs={12} sm={isKiemeltFromLanding ? 12 : 3} mb={isKiemeltFromLanding ? 0 : { xs: 2, lg: 0 }} alignSelf={isKiemeltFromLanding ? "start" : { xs: "start", lg: "center" }} justifyContent={"center"}>
          {isLoading ? (
            <FindSkeleton variant="rectangular" sx={{ margin: "auto" }}>
              <FindItemImage>
                <FindItemNoImage iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0}>
                  <Stack height={"100%"} direction={"column"} justifyContent={"center"} alignItems={"center"} >
                    <Stack mb={0}><ImageNotSupportedOutlinedIcon /></Stack>
                    <Stack><Typography dangerouslySetInnerHTML={{ __html: t('common.noImage') }} /></Stack>
                  </Stack>
                </FindItemNoImage>
              </FindItemImage>
            </FindSkeleton>
          ) : (
            hasUrl ? (
              <Link href={(type === PageType.RBhuPP ? item?.urlPP : item?.url) as string} passHref>
                <a onClick={() => handleSetPosition()}>
                  <FindItemImage iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0}>
                    {hasSzalagKiemeles ? <img className={`ribbon ${isKiemeltFromLanding ? 'ribbon-kiemelt' : ''}`} src={getSzalagImageUrl(item.kiemelesSzalag).ribbon} alt={t(pfSzalagKiemelesVariantTitle[item.kiemelesSzalag as PFSzalagKiemelesVariant])} /> : (<></>) }
                    {imageUrl ? (
                        <Image src={imageUrl} alt={capitalize(item?.title as string)} layout="fill" objectFit="contain" loading={preload ? "eager" : "lazy"} priority={preload} />
                      ) : (
                        <FindItemNoImage iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0}>
                          <Stack height={"100%"} direction={"column"} justifyContent={"center"} alignItems={"center"} >
                            <Stack mb={0}><ImageNotSupportedOutlinedIcon /></Stack>
                            <Stack><Typography dangerouslySetInnerHTML={{ __html: t('common.noImage') }} /></Stack>
                          </Stack>
                        </FindItemNoImage>
                      )}
                  </FindItemImage>
                </a>
              </Link>
            ) : (
              <FindItemImage iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0}>
                {hasSzalagKiemeles ? <img className={`ribbon ${isKiemeltFromLanding ? 'ribbon-kiemelt' : ''}`} src={getSzalagImageUrl(item.kiemelesSzalag).ribbon} alt={t(pfSzalagKiemelesVariantTitle[item.kiemelesSzalag as PFSzalagKiemelesVariant])} /> : (<></>) }
                {imageUrl ? (
                  <Image src={imageUrl} alt={capitalize(item?.title as string)} layout="fill" objectFit="contain" loading={preload ? "eager" : "lazy"} priority={preload} />
                ) : (
                  <FindItemNoImage iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0}>
                    <Stack height={"100%"} direction={"column"} justifyContent={"center"} alignItems={"center"} >
                      <Stack mb={0}><ImageNotSupportedOutlinedIcon /></Stack>
                      <Stack><Typography dangerouslySetInnerHTML={{ __html: t('common.noImage') }} /></Stack>
                    </Stack>
                  </FindItemNoImage>
                )}
              </FindItemImage>
            )
          )}
        </Grid>
        <Grid item xs={12} sm={isKiemeltFromLanding ? 12 : 9}>
          <Grid container height={"100%"} px={isKiemeltFromLanding ? 1 : 0}>
            <Grid item xs={12} sm={isKiemeltFromLanding ? 12 : 9} display={"flex"} flexDirection={"column"} pl={isKiemeltFromLanding ? 1 : { xs: 1, sm: 2 }} order={isKiemeltFromLanding ? 2 : undefined}>
            {isLoading ? (
              <FindSkeleton variant="rectangular" height={25} sx={{ mb: 0.5 }} />
            ) : (
              <FindTitle iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0} item={1}>
                {hasUrl ? (
                  <Link href={(type === PageType.RBhuPP ? item?.urlPP : item?.url) as string} passHref>
                    <StyledLink onClick={() => handleSetPosition()}>
                      {capitalize(item?.title as string)}
                    </StyledLink>
                  </Link>
                ) : (
                  capitalize(item?.title as string)
                )}
              </FindTitle>
            )}
            {hasDisplayedAt ? (
              <Grid container mb={0.5}>
                <Grid item xs={12}>
                  {isLoading ? (
                    <FindSkeleton variant="rectangular" sx={{ mb: 1 }}>
                      <FindItemDate>
                        <Stack mr={0.5}><AccessTimeIcon /></Stack>
                        <Stack>{moment(item?.displayedAt).format('LLL')}</Stack>
                      </FindItemDate>
                    </FindSkeleton>
                  ) : (
                    <FindItemDate>
                      <Stack mr={0.5}><AccessTimeIcon /></Stack>
                      <Stack>{moment(item?.displayedAt).format('LLL')}</Stack>
                    </FindItemDate>
                  )}
                </Grid>
              </Grid>
            ) : null}
            {hasKeyFactsOrText ? (
              <Grid container>
                {hasKeyFacts ? (
                  <Grid item xs={12} mb={2}>
                    <Stack display={"inline-block"}>
                      {item?.keyFacts?.map((fact, i) => {
                        return isLoading ? (
                          <FindSkeleton key={i} variant="rectangular" sx={{ display: 'inline-block', mr: 0.5 }}>
                            <FindItemFact sepcolor={colors.mainColor} dangerouslySetInnerHTML={{ __html: separatedFact(fact) }} />
                          </FindSkeleton>
                        ) : (
                          <FindItemFact key={i} sepcolor={colors.mainColor} justonefact={justOneKeyFact} dangerouslySetInnerHTML={{ __html: separatedFact(fact) }} />
                        )
                      })}
                    </Stack>
                  </Grid>
                ) : null}

                {hasText ? (
                  <Grid item xs={12} mb={1}>
                    {isLoading ? (
                      <FindSkeleton variant="rectangular">
                        <FindItemText dangerouslySetInnerHTML={{ __html: item.text as string }} />
                      </FindSkeleton>
                    ) : (
                      <FindItemText dangerouslySetInnerHTML={{ __html: item.text as string }} />
                    )}
                  </Grid>
                ) : null }
              </Grid>
            ) : null}
            {hasShipping ? (
              <Grid container display={"flex"} alignItems={"center"} mt={"auto"}>
                <Grid item xs>
                  <FindItemDeliveryContainer>
                    <>
                      {item?.shippingSzemelyes ? isLoading ? (<FindSkeleton variant="rectangular" width={24} height={24} sx={{ display: 'inline-block', mr: 0.5 }} />) : (<FindItemDeliveryItem color="#5bc0de"><RoomIcon fontSize="large" /></FindItemDeliveryItem>) : null }
                      {item?.shippingFutar ? isLoading ? (<FindSkeleton variant="rectangular" width={24} height={24} sx={{ display: 'inline-block' , mr: 0.5}} />) : (<FindItemDeliveryItem color="#d9534f"><LocalShippingIcon fontSize="large" /></FindItemDeliveryItem>) : null }
                      {item?.shippingPosta ? isLoading ? (<FindSkeleton variant="rectangular" width={24} height={24} sx={{ display: 'inline-block' }} />) :  (<FindItemDeliveryItem color="#5cb85c"><EmailIcon fontSize="large" /></FindItemDeliveryItem>) : null }
                    </>
                  </FindItemDeliveryContainer>
                </Grid>
              </Grid>
            ) : null}
            </Grid>
            <Grid item xs={12} sm={isKiemeltFromLanding ? 12 : 3} pl={1} pt={isKiemeltFromLanding ? 1 : { xs: 1, sm: 0 }} order={isKiemeltFromLanding ? 1 : undefined}>
              <Grid container direction={"row"} height={"100%"} alignItems={isKiemeltFromLanding ? "end" : undefined}>
                <Grid item xs={isKiemeltFromLanding ? 12 : 6} sm={12} my={isKiemeltFromLanding ? 1.5 : undefined} display={"flex"} justifyContent={isKiemeltFromLanding ? "start" : { xs: "start", sm: "end" }} alignItems={isKiemeltFromLanding ? "center" : { xs: "center", sm: "start" }}>
                  {isLoading ? (
                    <FindSkeleton variant="rectangular" sx={{ mb: 1 }}>
                      <FindItemPrice>
                        <Typography fontWeight={"bold"}>
                          {priceFormatter(item?.price?.amount, { currency: item?.price?.currency })}
                          {!isKiemeltFromLanding && hasQuantity ? (<span> / { item?.price?.quantity }</span>) : null}
                          {!isKiemeltFromLanding && hasFlags ? (<span> { getPriceFlagString(t, item?.price) }</span>) : null}
                        </Typography>
                      </FindItemPrice>
                    </FindSkeleton>
                  ) : (
                    <FindItemPrice iskiemeltfromlanding={isKiemeltFromLanding ? 1 : 0}>
                      <Typography fontWeight={"bold"}>
                        {priceFormatter(item?.price?.amount, { currency: item?.price?.currency })}
                        {!isKiemeltFromLanding && hasQuantity ? (<span> / { t(priceQuantityTitle[item?.price?.quantity as PriceQuantity]).toLowerCase() }</span>) : <span></span>}
                        {!isKiemeltFromLanding && hasFlags ? (<span> { getPriceFlagString(t, item?.price) }</span>) : <span></span>}
                      </Typography>
                    </FindItemPrice>
                  )}
                </Grid>
                {type === PageType.RBhuPP ? (
                  <Grid item xs={6} sm={12} mt={"auto"} display={"flex"} justifyContent={"end"} pr={{xs: 1, sm: 0}}>
                    {isLoading ? (
                      <FindSkeleton variant="rectangular" width={24} height={24} />
                    ) : (
                      <Link href={item?.urlPP as string} passHref>
                        <a onClick={() => handleSetPosition()}>
                          <FindButton
                            bgcolor={colors.mainColor}
                            bghovercolor={colors.secondaryColor}
                            fullWidth={false}
                            itembutton={1}
                            variant="contained"
                          >
                            <ChevronRightIcon fontSize="large" />
                          </FindButton>
                        </a>
                      </Link>
                    )}
                  </Grid>
                ) : (
                  setFavButton ? (
                    <Grid item xs={6} sm={isKiemeltFromLanding ? 6 : 12} mt={"auto"} display={"flex"} justifyContent={"end"} pr={isKiemeltFromLanding ? 1 : {xs: 1, sm: 0}}>
                      <IconButton size={"small"} sx={{ p: isKiemeltFromLanding ? 0 : undefined }}>
                        <FavoriteBorderIcon />
                      </IconButton>
                    </Grid>
                  ) : null
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FindItemContainer>
    </>
  )
}

export default FindItem;